import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { useLocation } from 'react-router-dom';
import { UserOutlined, TeamOutlined } from '@ant-design/icons';
import Table from '@/components/ui/table/Table';
import { ErrorBlock, DetailPanelLoading } from './style';
import { useClientsModule, CLIENTS_VIEW } from '@hooks/clients/useClients';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import ClientDetailModal from '@/components/module/clients/ClientDetailModal';

export default function Clients() {
    const location = useLocation();
    const isInhouseRoute = useMemo(() => String(location.pathname).endsWith('/clients/inhouse'), [location.pathname]);
    const view = isInhouseRoute ? CLIENTS_VIEW.INHOUSE : CLIENTS_VIEW.ALL;

    const {
        searchText,
        setSearchText,
        columns,
        rows,
        openClient,
        openProject,
        selectedClient,
        detailModalOpen,
        closeDetailModal,
        detailLoading,
        isLoading,
        isError,
        page,
        limit,
        total,
        setPage,
        setLimit,
    } = useClientsModule(view);

    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }

    if (isError) {
        return (
            <ListBodyLayout title="Client">
                <ErrorBlock>Failed to load projects.</ErrorBlock>
            </ListBodyLayout>
        );
    }

    return (
        <ListBodyLayout
            title="Client"
            description={view === CLIENTS_VIEW.INHOUSE ? 'In-house clients stored in the tenant directory.' : 'All clients in the tenant directory.'}
            kpiCards={[
                {
                    label: 'Total Clients',
                    value: total ?? 0,
                    icon: <UserOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'all time',
                    trendPositive: true,
                },
                {
                    label: 'Mode',
                    value: view === CLIENTS_VIEW.INHOUSE ? 'Inhouse' : 'All',
                    icon: <TeamOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'sidebar',
                    trendPositive: true,
                },
            ]}
        >
            <Table
                title="Clients"
                columns={columns}
                dataSource={rows}
                searchValue={searchText}
                onSearchChange={(e) => setSearchText(e?.target?.value ?? '')}
                showFilter={false}
                showMore={false}
                paginationTotal={total}
                paginationCurrent={page}
                pageSize={limit}
                onPaginationChange={(nextPage, nextLimit) => {
                    setPage(nextPage);
                    if (nextLimit !== limit) setLimit(nextLimit);
                }}
                onRow={(record) => ({
                    onClick: () => openClient(record?.id),
                    style: { cursor: 'pointer' },
                })}
            />

            <ClientDetailModal
                open={detailModalOpen}
                client={selectedClient}
                loading={detailLoading}
                onProjectClick={openProject}
                onClose={closeDetailModal}
            />
        </ListBodyLayout>
    );
}
